import React, { useEffect, useRef, useState } from 'react';
import { Map } from 'immutable';
import moment, { Moment } from 'moment';

import { createEvent } from 'flow/actions/calendar-actions';
import CalendarOccurrenceDropdown from 'flow/components/calendar/CalendarOccurrenceDropdown';
import CalendarProfilePicker from 'flow/components/calendar/CalendarProfilePicker';
import SettingSection from 'flow/components/Setting/SettingSection';
import BaseInput from 'flow/components/ui-components/BaseInput';
import BaseModal from 'flow/components/ui-components/BaseModal';
import TimeInput from 'flow/components/ui-components/TimeInput';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';
import EmbeddedType from 'flow/types/embedded-type';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { isExtensionKey, ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { isAllDayEvent } from 'flow/utils/calendar-utils';

interface Props {
    entityKey?: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    start?: Moment;
    end?: Moment;
    profile?: TypedMap<Profile>;
}

const CreateCalendarEventModal = ({
    entityKey,
    start,
    end,
    profile,
}: Props) => {
    const { embedded, i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const startDate = moment(start).format('YYYY-MM-DD');

    const [calendarEvent] = useState(Map());

    const [selectProfile, setSelectProfile] = useState({
        id: null,
        name: i18next.t('calendar.select.profile'),
    });

    const [allDay, setAllDay] = useState(isAllDayEvent(calendarEvent));

    const [repeat, setRepeat] = useState({
        occurrence: null,
        interval: null,
        name: i18next.t('calendar.occurence.never'),
    });

    const timeRefStart = useRef<HTMLInputElement>(null);
    const dateRefStart = useRef<HTMLInputElement>(null);

    const timeRefEnd = useRef<HTMLInputElement>(null);
    const dateRefEnd = useRef<HTMLInputElement>(null);

    const [formattedTime, setFormattedTime] = useState({
        start: moment(start || new Date()).format(ISO_8601_MS_RESOLUTION),
        end: moment(end || new Date()).format(ISO_8601_MS_RESOLUTION),
    });

    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setDateTime();
    }, [allDay]);

    useEffect(() => {
        if (profile) {
            setSelectProfile({
                id: profile.get('key'),
                name: profile.get('description'),
            });
        }
    }, [profile]);

    const setDateTime = () => {
        if (allDay) {
            const startMoment = moment(
                `${dateRefStart.current?.value} 00:00`,
                'YYYY-MM-DD HH:mm',
            );
            if (!startMoment.isValid()) return;

            setFormattedTime({
                start: startMoment.format(ISO_8601_MS_RESOLUTION),
                end: startMoment
                    .clone()
                    .add(1, 'days')
                    .format(ISO_8601_MS_RESOLUTION),
            });
        } else {
            const startMoment = moment(
                `${dateRefStart.current?.value} ${timeRefStart.current?.value}`,
                'YYYY-MM-DD HH:mm',
            );
            const endMoment = moment(
                `${dateRefEnd.current?.value} ${timeRefEnd.current?.value}`,
                'YYYY-MM-DD HH:mm',
            );
            if (!startMoment.isValid() || !endMoment.isValid()) return;

            setFormattedTime({
                start: startMoment.format(ISO_8601_MS_RESOLUTION),
                end: endMoment.format(ISO_8601_MS_RESOLUTION),
            });
        }
    };

    const onSubmit = () => {
        const newCalendarEvent = calendarEvent
            .set('profile', { key: selectProfile.id })
            .set('start', formattedTime.start)
            .set('end', formattedTime.end)
            .set('occurence', repeat.occurrence)
            .set('interval', repeat.interval)
            .delete('rrule');

        dispatch(createEvent(newCalendarEvent, entityKey));
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('calendar.create-new-event')}
            onSubmit={onSubmit}
            disableSubmit={
                !selectProfile.id ||
                formattedTime?.start >= formattedTime?.end ||
                moment().format(ISO_8601_MS_RESOLUTION) >= formattedTime?.end
            }
        >
            <div className="flex flex-col gap-3 pb-1">
                <div>
                    <SettingSection className="px-0 text-sm/4">
                        {isExtensionKey(entityKey)
                            ? i18next.t('calendar.profile')
                            : i18next.t('pbx.pbxmode')}
                    </SettingSection>

                    <CalendarProfilePicker
                        profile={selectProfile}
                        onChange={setSelectProfile}
                        entityKey={entityKey}
                    />
                </div>
                <div className="flex w-full items-center justify-between rounded-sm text-base hover:bg-gray-100">
                    <p>{i18next.t('calendar.all.day')}</p>

                    <Toggle
                        value={allDay}
                        onChange={() => {
                            setAllDay(!allDay);
                            calendarEvent.set('allDay', allDay);
                        }}
                        text={i18next.t('calendar.all.day')}
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-3">
                        <SettingSection className="px-0 text-sm/4">
                            {i18next.t('calendar.start')}
                        </SettingSection>

                        <BaseInput
                            type="date"
                            ref={dateRefStart}
                            defaultValue={startDate}
                            min={startDate}
                            onChange={setDateTime}
                            {...(embedded === EmbeddedType.IOS && {
                                role: 'textbox',
                                'aria-label': moment(
                                    formattedTime.start,
                                ).format('MMMM D, YYYY'),
                            })}
                        />

                        {!allDay && (
                            <TimeInput
                                ref={timeRefStart}
                                defaultValue={start?.format('HH:mm') || '08:00'}
                                onChange={setDateTime}
                                ariaLabel={moment(formattedTime.start).format(
                                    'HH:mm',
                                )}
                            />
                        )}
                    </div>

                    {!allDay && (
                        <div className="flex flex-col gap-3">
                            <SettingSection className="px-0 text-sm/4">
                                {i18next.t('calendar.end')}
                            </SettingSection>

                            <BaseInput
                                type="date"
                                ref={dateRefEnd}
                                defaultValue={moment(end).format('YYYY-MM-DD')}
                                min={startDate}
                                onChange={setDateTime}
                                {...(embedded === EmbeddedType.IOS && {
                                    role: 'textbox',
                                    'aria-label': moment(
                                        formattedTime.end,
                                    ).format('MMMM D, YYYY'),
                                })}
                            />
                            <TimeInput
                                ref={timeRefEnd}
                                defaultValue={end?.format('HH:mm') || '08:30'}
                                onChange={setDateTime}
                                ariaLabel={moment(formattedTime.end).format(
                                    'HH:mm',
                                )}
                            />
                        </div>
                    )}
                </div>

                <div>
                    <SettingSection className="px-0 text-sm/4">
                        {i18next.t('calendar.repeat')}
                    </SettingSection>

                    <CalendarOccurrenceDropdown
                        repeat={repeat}
                        onChange={setRepeat}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default CreateCalendarEventModal;
